//
import React from "react";
import { ReactNode } from "react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ChevronDown, ChevronRight } from "lucide-react";

interface NavGroupProps {
  label: string;
  icon?: React.ElementType;
  children: ReactNode;
  isCollapsed?: boolean;
  collapsed?: boolean;
  disabled?: boolean;
  disabledMessage?: string;
  onToggleCollapse?: () => void;
}

export const NavGroup = (props: NavGroupProps) => {
  const {
    label,
    icon: IconComponent,
    children,
    isCollapsed = false,
    collapsed = false,
    disabled = false,
    disabledMessage,
    onToggleCollapse,
  } = props;

  const handleToggleCollapse = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onToggleCollapse) {
      onToggleCollapse();
    }
  };

  const groupContent = (
    <>
      {label && (
        <div
          className={cn(
            "px-3 text-xs font-semibold uppercase flex items-center gap-2",
            "transform translate-x-[-11px]",
            "tracking-widest mb-2",
            disabled ? "text-muted-foreground/50" : "text-muted-foreground",
            "text-left",
            "cursor-pointer"
          )}
          onClick={handleToggleCollapse}
        >
          {IconComponent && <IconComponent className="h-4 w-4" />}
          <span>{label}</span>
          {!isCollapsed && onToggleCollapse && (
            <div className="ml-auto">
              {collapsed ? (
                <ChevronRight className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
            </div>
          )}
        </div>
      )}
      <div
        className={cn(
          "flex flex-col gap-1 w-full",
          isCollapsed && "items-center",
          !isCollapsed && collapsed && "hidden"
        )}
      >
        {!isCollapsed ? (
          <div className="relative ml-2 pl-3 border-l border-gray-400 flex flex-col gap-1">
            {children}
          </div>
        ) : (
          children
        )}
      </div>
    </>
  );

  // When collapsed with a label, show the first letter with a tooltip
  if (isCollapsed && label) {
    return (
      <div className="w-full flex flex-col items-center">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className="w-full text-center mb-2 cursor-pointer"
                onClick={handleToggleCollapse}
              >
                <div
                  className={cn(
                    "text-xs font-semibold uppercase tracking-widest flex items-center justify-center gap-1",
                    disabled
                      ? "text-muted-foreground/50"
                      : "text-muted-foreground"
                  )}
                >
                  {IconComponent ? (
                    <IconComponent className="h-4 w-4" />
                  ) : (
                    label.charAt(0)
                  )}
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>{label}</p>
              {disabled && disabledMessage && (
                <p className="text-xs text-muted-foreground">
                  {disabledMessage}
                </p>
              )}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <div
          className={cn(
            "flex flex-col gap-1 w-full items-center",
            collapsed && "hidden"
          )}
        >
          {children}
        </div>
      </div>
    );
  }

  // When expanded and disabled, show the disabled message in a tooltip
  if (disabled && disabledMessage && !isCollapsed) {
    return (
      <div
        className={cn(
          "w-full flex flex-col",
          isCollapsed ? "items-center" : "items-start"
        )}
      >
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger className="w-full">{groupContent}</TooltipTrigger>
            <TooltipContent side="right">
              <p>{disabledMessage}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  }

  // Standard display
  return (
    <div
      className={cn(
        "w-full flex flex-col",
        isCollapsed ? "items-center" : "items-start"
      )}
    >
      {groupContent}
    </div>
  );
};
